import * as queryString from 'qs';
import { autoinject } from 'aurelia-dependency-injection';

import { TokenService } from './TokenService';
import { TokenSecurityContextProvider, ISecurityContext } from '@asuresoftware/asure.auth';

const buildOAuthPreviousHref = () => {

  const { origin, pathname, hash, search, href } = window.location;

  // never save logout route or we'll create an infinite loop
  if (hash === '#/logout') {
    return origin;
  }

  if (!search) {
    return href;
  }

  // sanitize the query-string to prevent a loop
  const qs = queryString.parse(search, { ignoreQueryPrefix: true });
  delete qs.access_token;

  const cleanSearch = queryString.stringify(qs);
  return `${origin}${pathname}${cleanSearch}${hash}`;
};

export class AccountServiceConfig {
  ssoDomain: string;
}

@autoinject
export class AccountService {
  constructor(private config: AccountServiceConfig, private tokenService: TokenService, private securityContextProvider: TokenSecurityContextProvider) { }

  async authenticateViaRedirect() {
    const response = await this.tokenService.requestSSOToken();
    const { ssoToken } = response;
    window.sessionStorage.setItem('oauth.previous-href', buildOAuthPreviousHref());
    window.location.replace(`${this.config.ssoDomain}/?jwt=${ssoToken}&shared_session=true&response_type=token`);

    // stop here - never resolve
    await new Promise(resolve => { });
  }

  private async authenticateViaStorage() {
    return this.tokenService.getAccessToken();
  }

  private async authenticateViaQueryParam() {
    const qs = queryString.parse(window.location.search, { ignoreQueryPrefix: true });
    const accessToken = qs.access_token;
    if (accessToken) {
      this.tokenService.setAccessToken(accessToken);
      const previousHref = window.sessionStorage.getItem('oauth.previous-href') || '/';
      window.location.replace(previousHref);

      // stop here - never resolve (we're reloading the page with the new user)
      await new Promise(resolve => { });
    }
  }

  async login(): Promise<ISecurityContext> {
    // First, check for access token in query string. If found, user is logging in,
    // so reload the page and stop here. Otherwise, proceed to alternatives below.
    await this.authenticateViaQueryParam();

    // Next, check for access token in session storage. If found, user is already
    // logged in, so extract user info and we're done.
    const accessToken = await this.authenticateViaStorage();
    if (accessToken) {
      const securityContext = this.securityContextProvider.getUnverifiedContext({ accessToken });
      if (securityContext) {
        return securityContext;
      }
    }

    // No valid access token, so we need to redirect to the login page.
    // this will intentionally never resolve as it waits for the redirect
    return await this.authenticateViaRedirect() as any;
  }

  async tryOrLogin<T>(request: () => Promise<T>): Promise<T> {
    try {
      return await request();
    } catch (e) {
      if (e.status === 401) {
        await this.authenticateViaRedirect();
      }
      throw e;
    }
  }

  async logout() {
    // clear portal session
    this.tokenService.clearAccessToken();

    // clear prev href so we go to the default landing page if we log back in
    window.sessionStorage.removeItem('oauth.previous-href');

    // redirect to heisenberg logout
    const { ssoToken } = await this.tokenService.requestSSOToken();
    window.location.replace(`${this.config.ssoDomain}/signout?jwt=${ssoToken}&response_type=token`);

    // stop here - never resolve
    await new Promise(resolve => { });
  }

}



// WEBPACK FOOTER //
// ./src/support/AccountService.ts